/**
 * Anemoia Museum (custom) — WebGL wooden-house gallery (kellydev.io-style).
 *
 * A Three.js scene built procedurally: a long wooden house above a green field.
 * Gold-framed photos hang on the dark-wood gallery wall; the opposite wall has
 * two big window openings onto the horizon. ARROW buttons glide the camera
 * between waypoints in front of each artwork; clicking a framed photo (or
 * "View details") opens its description; closing returns to browsing. Past the
 * last frame the camera squares up to the far window while the closing line
 * invites visitors to send their own artifacts. Frames with no dossier yet hang
 * behind a curtain that breathes on a slow vertex wave.
 *
 * Progressive enhancement: if Three.js or WebGL is unavailable the element keeps
 * the CSS `.museum__fallback` grid (this script simply never adds `.is-webgl`).
 */

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class AnemoiaMuseum extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('[data-museum-canvas]');
    const dataEl = this.querySelector('[data-museum-photos]');
    try {
      this.photos = JSON.parse(dataEl?.textContent || '[]').filter(Boolean);
    } catch (e) {
      this.photos = [];
      console.warn('Anemoia museum: photo JSON failed to parse', e, dataEl?.textContent);
    }

    this.setupCursor();
    this.setupDive();

    this.houseEl = this.querySelector('[data-museum-house]');
    this.houseImg = this.querySelector('.museum__house-img');

    // "Knock Knock" door — bound here (not in bindUI) so entering still works
    // when WebGL is unavailable and the fallback grid sits underneath. The
    // whole house screen is the target (a phone shouldn't have to hit the small
    // "Knock Knock" line); the button stays for keyboard and screen readers and
    // its click just bubbles up to the same handler.
    this.knockBtn = this.querySelector('[data-museum-knock]');
    this.onKnock = () => this.enterHouse();
    this.houseEl?.addEventListener('click', this.onKnock);

    // Three.js loads as a deferred classic script; if it hasn't executed yet,
    // retry once the window finishes loading instead of bailing straight away.
    if (!window.THREE && document.readyState !== 'complete') {
      window.addEventListener('load', () => this.boot(), { once: true });
      return;
    }
    this.boot();
  }

  /** Deterministic fly-in: find the door's on-screen pixel (door focal = % of
      the house IMAGE, mapped through the object-fit:cover crop) and animate a
      translate+scale that ends with the door at the CENTRE of the screen — the
      door cannot be missed regardless of viewport shape.
      With transform-origin O and `translate(T) scale(s)`, a point P lands at
      T + O + s·(P−O); for the door P = O it lands exactly at O + T = centre. */
  flyIntoDoor() {
    const img = this.houseImg;
    const el = this.houseEl;
    if (!img || !el || !img.naturalWidth) return; // CSS fallback zoom stands in
    const kx = parseFloat(getComputedStyle(el).getPropertyValue('--knock-x')) || 50;
    const ky = parseFloat(getComputedStyle(el).getPropertyValue('--knock-y')) || 62;
    const vw = el.clientWidth || window.innerWidth;
    const vh = el.clientHeight || window.innerHeight;
    const scale = Math.max(vw / img.naturalWidth, vh / img.naturalHeight);
    const dw = img.naturalWidth * scale;
    const dh = img.naturalHeight * scale;
    const px = (kx / 100) * dw - (dw - vw) / 2;  // door in element-space px
    const py = (ky / 100) * dh - (dh - vh) / 2;
    img.style.transformOrigin = `${px.toFixed(1)}px ${py.toFixed(1)}px`;
    void img.offsetWidth; // commit the origin before the transform animates
    const tx = vw / 2 - px;
    const ty = vh / 2 - py;
    img.style.transform = `translate(${tx.toFixed(1)}px, ${ty.toFixed(1)}px) scale(9)`;
  }

  /** Knock → fly into the door (JS zoom), then reveal the gallery and walk in. */
  enterHouse() {
    if (this.entered) return;
    this.entered = true;
    this.flyIntoDoor();
    this.classList.add('is-knocking');
    this.enterTimer = window.setTimeout(() => {
      this.classList.add('is-entered');
      if (this.booted) this.goTo(0); // the walk-in glide to the first frame
    }, 1450);
  }

  boot() {
    if (this.booted) return;
    // Bail to the CSS fallback grid if Three.js / WebGL isn't available.
    if (!window.THREE) return console.warn('Anemoia museum: THREE not loaded — fallback grid');
    if (!this.canvas) return console.warn('Anemoia museum: canvas missing — fallback grid');
    if (!this.photos.length) return console.warn('Anemoia museum: no photos — fallback grid');
    if (!this.hasWebGL()) return console.warn('Anemoia museum: WebGL unavailable — fallback grid');

    /* `is-webgl` FIRST, because it is what pins this element to the viewport
       height. Without it `.museum:not(.is-webgl)` is `height: auto`, so the box
       is as tall as the fallback grid underneath — thousands of pixels on a
       phone — and initScene's closing resize() measures an aspect of about 0.12
       instead of 0.58. That wrong aspect used to merely stretch the render (the
       frame looked too wide and its side rails ran off screen); once fitFrame()
       started acting on it, it threw the camera 23 units back, outside the
       house entirely. Removed again if the scene fails to build, so the
       fallback grid still gets its chance. */
    this.classList.add('is-webgl');
    try {
      this.initScene();
    } catch (e) {
      console.warn('Anemoia museum: WebGL init failed', e);
      this.classList.remove('is-webgl');
      return; // fallback grid stays
    }
    this.booted = true;

    this.resize(); // once more now the layout is final, before the first frame
    this.bindUI();
    this.index = 0;
    this.atEnd = false;
    this.updateMeta();
    // If the visitor already knocked while we were still booting, walk in now.
    if (this.classList.contains('is-entered')) this.goTo(0);
    this.animate = this.animate.bind(this);
    this.raf = requestAnimationFrame(this.animate);

    this.onResize = () => this.resize();
    window.addEventListener('resize', this.onResize, { passive: true });
    // (resumeScene refuses to restart while the dive covers the gallery.)
    this.onVisibility = () => {
      if (document.hidden) this.pauseScene();
      else this.resumeScene();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  hasWebGL() {
    try {
      const c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  }

  initScene() {
    const THREE = window.THREE;
    const N = this.photos.length;
    const artScale = Number(this.dataset.artScale) || 1;
    const gap = Number(this.dataset.gap) || 4.5; // spacing between frames along the wall
    this.camDist = Number(this.dataset.camDist) || 4.5; // camera distance from the gallery wall
    this.camDistBase = this.camDist; // the pinned distance; fitFrame() only ever adds to it

    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    // Phones: cap the pixel ratio lower — high-DPR small screens don't need
    // the extra pixels and the cheaper frame keeps the camera glide smooth.
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isTouch ? 1.5 : 2));
    this.renderer.outputEncoding = THREE.sRGBEncoding;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xbfd8de);
    this.camera = new THREE.PerspectiveCamera(58, 1, 0.1, 500);

    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    const maxAniso = this.renderer.capabilities.getMaxAnisotropy?.() || 1;
    const srgb = (t) => { t.encoding = THREE.sRGBEncoding; t.anisotropy = maxAniso; return t; };

    /* ---- The wooden house (kellydev-style long gallery room) ----------
       Gallery wall with the frames on the RIGHT (+x); window wall on the
       LEFT (-x) with TWO big openings, both onto the green-field horizon.
       Camera glides along the aisle between waypoints in front of each frame. */
    const W = 11;                       // room width (x)
    const floorY = -1.7;
    const ceilY = 2.9;                  // wall height ≈ 4.6
    const pad = 4;                      // dead space at both ends
    const zFirst = 0;                   // first frame z; frames march toward +z,
    const zLast = (N - 1) * gap;        // which is CAMERA-RIGHT when facing the
    const zNear = zFirst - pad;         // gallery wall — so the next (›) arrow
    const zFar = zLast + pad;           // visibly moves right, prev (‹) left.
    const L = zFar - zNear;             // room length
    const zMid = (zNear + zFar) / 2;

    const wallX = W / 2;
    this.wallX = wallX;

    // Lighting: soft ambient + a warm point light over each artwork station.
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.66));
    this.scene.add(new THREE.HemisphereLight(0xfff6e6, 0x8a7a5a, 0.34));
    for (let i = 0; i < N; i++) {
      const p = new THREE.PointLight(0xffe9c4, 0.5, 14, 1.6);
      p.position.set(0.5, ceilY - 0.5, i * gap);
      this.scene.add(p);
    }

    // Materials.
    const floorTex = srgb(loader.load(this.dataset.floor));
    floorTex.wrapS = floorTex.wrapT = THREE.RepeatWrapping;
    floorTex.repeat.set(W / 3, L / 3);
    const floorMat = new THREE.MeshLambertMaterial({ map: floorTex });

    const wallTex = srgb(loader.load(this.dataset.wall));
    wallTex.wrapS = wallTex.wrapT = THREE.RepeatWrapping;
    wallTex.repeat.set(L / 3, 1.6);
    const wallMat = new THREE.MeshLambertMaterial({ map: wallTex });

    const wallTexEnd = srgb(loader.load(this.dataset.wall));
    wallTexEnd.wrapS = wallTexEnd.wrapT = THREE.RepeatWrapping;
    wallTexEnd.repeat.set(W / 3, 1.6);
    const wallMatEnd = new THREE.MeshLambertMaterial({ map: wallTexEnd });

    const ceilMat = new THREE.MeshLambertMaterial({ color: 0xefe7d8 });

    const box = (w, h, d, mat, x, y, z) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      this.scene.add(m);
      return m;
    };

    // Floor + ceiling.
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(W + 0.4, L + 0.4), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, floorY, zMid);
    this.scene.add(floor);

    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(W + 0.4, L + 0.4), ceilMat);
    ceil.rotation.x = Math.PI / 2;
    ceil.position.set(0, ceilY, zMid);
    this.scene.add(ceil);

    // Ceiling spotlights (emissive discs, like the reference's downlights).
    const spotMat = new THREE.MeshBasicMaterial({ color: 0xfff3d6 });
    for (let z = zNear + 1.5; z < zFar - 1; z += 2.4) {
      for (const x of [-W / 4, W / 4]) {
        const disc = new THREE.Mesh(new THREE.CircleGeometry(0.16, 20), spotMat);
        disc.rotation.x = Math.PI / 2;
        disc.position.set(x, ceilY - 0.02, z);
        this.scene.add(disc);
      }
    }

    // Gallery wall (right, +x) and end walls — dark wood panelling.
    box(0.2, ceilY - floorY, L, wallMat, wallX + 0.1, (floorY + ceilY) / 2, zMid);
    box(W + 0.4, ceilY - floorY, 0.2, wallMatEnd, 0, (floorY + ceilY) / 2, zNear + 0.1);
    box(W + 0.4, ceilY - floorY, 0.2, wallMatEnd, 0, (floorY + ceilY) / 2, zFar - 0.1);

    /* Window wall (left, -x): sill + lintel strips with ONE big opening at the
       far end, which is the one the closing shot faces. The near half is solid
       panelling — a second opening there only ever showed as a stray sliver of
       sky past the pier, beside the closing view. */
    const sillTop = floorY + 1.0;
    const lintelBot = ceilY - 0.55;
    const wx = -wallX - 0.1;
    box(0.2, sillTop - floorY, L, wallMat, wx, (floorY + sillTop) / 2, zMid);                   // sill
    box(0.2, ceilY - lintelBot, L, wallMat, wx, (lintelBot + ceilY) / 2, zMid);                  // lintel
    const openH = lintelBot - sillTop;
    const openY = (sillTop + lintelBot) / 2;
    const openZ0 = zMid;        // the window's near jamb
    const openZ1 = zFar - 0.7;  // the inner face of the end pier
    box(0.2, openH, openZ0 - zNear, wallMat, wx, openY, (zNear + openZ0) / 2);                   // solid near half
    box(0.2, openH, 0.7, wallMat, wx, openY, zFar - 0.35);                                       // far pier

    /* Outside the windows: ONE full-length green-field horizon, and nothing
       else. There used to be a second jungle plane covering the near opening,
       which is what kept putting a wall of palm trees in the closing shot — it
       is gone. A single full-length backdrop also means no gap of raw
       background colour can show at an oblique angle through a window. */
    const viewFar = new THREE.Mesh(
      new THREE.PlaneGeometry(L + 12, 14),
      new THREE.MeshBasicMaterial({ map: srgb(loader.load(this.dataset.viewFar)) })
    );
    viewFar.rotation.y = Math.PI / 2;
    viewFar.position.set(-wallX - 4.9, openY + 0.8, zMid);
    this.scene.add(viewFar);

    /* Framed photos on the gallery wall, facing into the room (-x).
       LOCKED SIZE: how big a frame reads on screen is fixed by `art_scale` and
       `cam_dist` alone (the camera always stops the same distance out, square to
       the wall). It has never depended on how many frames hang there, and the
       three geometry settings are pinned in templates/page.museum.json so adding
       exhibits cannot move them. Only change them if you want every frame to
       change together. */
    const frameTex = loader.load(this.dataset.frame);
    frameTex.anisotropy = maxAniso;
    const fw = 2.4 * artScale;
    const fh = fw / (760 / 999);
    this.artW = fw; // fitFrame() needs these to keep the frame on screen
    this.artH = fh;
    const y0 = 0.25;

    // Frames with no dossier written yet hang behind a curtain.
    const curtainTex = this.photos.some((p) => p.locked) && this.dataset.curtain
      ? srgb(loader.load(this.dataset.curtain))
      : null;

    this.artMeshes = [];
    this.waypoints = [];
    this.curtains = [];
    for (let i = 0; i < N; i++) {
      const z = i * gap;
      const group = new THREE.Group();
      group.position.set(wallX - 0.12, y0, z);
      group.rotation.y = -Math.PI / 2;

      const pw = fw * 0.76;
      const ph = fh * 0.76;
      const photoMat = this.photos[i].img
        ? new THREE.MeshBasicMaterial({ map: srgb(loader.load(this.photos[i].img)) })
        : new THREE.MeshBasicMaterial({ color: 0x241d14 });
      const photo = new THREE.Mesh(new THREE.PlaneGeometry(pw, ph), photoMat);
      photo.userData.index = i;
      photo.userData.pw = pw;
      photo.userData.ph = ph;
      group.add(photo);

      const frame = new THREE.Mesh(
        new THREE.PlaneGeometry(fw, fh),
        new THREE.MeshBasicMaterial({ map: frameTex, transparent: true, alphaTest: 0.5, depthWrite: false })
      );
      frame.position.z = 0.05;
      frame.renderOrder = 1;
      group.add(frame);

      /* The curtain hangs IN FRONT of the whole frame, rod and all, solved from
         two constraints rather than eyeballed: the ROD lands just above the top
         of the gold frame, and the CLOTH still covers the whole picture opening.
         Both landmarks are read off the artwork — in anemoia-museum-curtain.png
         the rod's centre sits 2.7% down the image and the hem ends at 97.2% — so
         the plane's height and its offset fall straight out of them and hold if
         the frame size ever changes. Re-measure all three if the art is ever
         swapped again: they are properties of THAT file, not of the scene, and
         a new file with a different aspect hangs wrong until they are updated. */
      if (curtainTex && this.photos[i].locked) {
        const ROD = 0.027;          // rod centre, as a share of the artwork height
        const HEM = 0.972;          // where the cloth's hem ends
        const RATIO = 1536 / 1024;  // the artwork's own aspect
        const GATHER = 0.94;        // a slight horizontal gather, so neighbouring
                                    // rods never touch at the pinned 4.5 spacing
        const rodY = fh / 2 + 0.04;   // just clear of the frame's top rail
        const hemY = -ph / 2 - 0.03;  // just below the opening
        const ch = (rodY - hemY) / (HEM - ROD);
        const cw = ch * RATIO * GATHER;

        const curtain = new THREE.Mesh(
          new THREE.PlaneGeometry(cw, ch, 22, 16), // segmented so the cloth can move
          new THREE.MeshBasicMaterial({ map: curtainTex, transparent: true, alphaTest: 0.04, depthWrite: false })
        );
        curtain.position.set(0, rodY - (0.5 - ROD) * ch, 0.09);
        curtain.renderOrder = 2;
        curtain.userData.rest = curtain.geometry.attributes.position.array.slice();
        curtain.userData.ch = ch;
        curtain.userData.amp = ch * 0.022;
        curtain.userData.phase = i * 1.7; // neighbours breathe out of step
        group.add(curtain);
        this.curtains.push(curtain);
      }

      this.scene.add(group);
      this.artMeshes.push(photo);

      // Camera waypoint: stand in the aisle facing this frame head-on.
      this.waypoints.push({
        pos: new THREE.Vector3(wallX - this.camDist, 0, z),
        look: new THREE.Vector3(wallX, y0, z),
      });
    }

    /* Closing waypoint: stand in the aisle facing the window head-on and admire
       the green horizon. Squared up on purpose — an oblique angle turns the
       opening into a skewed wedge of sill and lintel, which is what the old
       "drift diagonally toward the glass" waypoint did once the room got short.
       Centred on the opening itself, so it holds for any number of frames. */
    const zWindow = (openZ0 + openZ1) / 2;
    this.endWaypoint = {
      pos: new THREE.Vector3(wallX - this.camDist, 0.15, zWindow),
      look: new THREE.Vector3(-wallX, openY, zWindow),
    };

    // Camera starts just inside the door and HOLDS until the visitor knocks;
    // entering then glides it to the first frame (the walk-in moment).
    this.camPos = new THREE.Vector3(0, 0, zFirst - 2.5);
    this.camLook = new THREE.Vector3(wallX, y0, zFirst);
    this.goalPos = this.camPos.clone();
    this.goalLook = this.camLook.clone();
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.camLook);

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.resize();
  }

  bindUI() {
    this.prevBtn = this.querySelector('[data-museum-prev]');
    this.nextBtn = this.querySelector('[data-museum-next]');
    this.hoverBtn = this.querySelector('[data-museum-frame-btn]');
    this.metaTitle = this.querySelector('[data-museum-metatitle]');
    this.metaCount = this.querySelector('[data-museum-count]');
    this.readEl = this.querySelector('[data-museum-read]');
    this.readTitle = this.querySelector('[data-museum-read-title]');
    this.readStory = this.querySelector('[data-museum-read-story]');
    this.closeBtn = this.querySelector('[data-museum-read-close]');

    // Touch (no hover): the pill is pinned to the CURRENT frame instead of
    // following a hover, and doubles as the Close control while reading.
    this.touchUI = window.matchMedia('(hover: none)').matches;

    this.onPrev = () => this.prev();
    this.onNext = () => this.next();
    this.onClose = () => this.closeRead();
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.closeBtn?.addEventListener('click', this.onClose);

    // The in-frame "View Details" pill. A frame that carries an exhibit dossier
    // dives INTO the artwork (CRT switch-on → journal); anything else keeps the
    // old on-frame description. On touch the pill reads "Close" while open.
    this.onHoverBtn = () => {
      if (this.classList.contains('is-diving')) return;
      if (this.classList.contains('is-reading')) return this.closeRead();
      const i = this.touchUI ? this.index : this.hoverIndex;
      if (i == null || this.photos[i]?.locked) return; // still behind the curtain
      if (this.hasDossier(i)) this.openDive(i);
      else this.openRead(i);
    };
    this.hoverBtn?.addEventListener('click', this.onHoverBtn);

    const pick = (event) => {
      const rect = this.canvas.getBoundingClientRect();
      this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      this.raycaster.setFromCamera(this.pointer, this.camera);
      return this.raycaster.intersectObjects(this.artMeshes)[0];
    };

    // Hover a frame → show the pill inside it (positioned each frame in animate).
    // Touch skips this: updateOverlays pins the pill to the current frame.
    this.onCanvasMove = (event) => {
      if (this.touchUI) return;
      if (!this.classList.contains('is-entered') || this.classList.contains('is-reading')) {
        this.hoverIndex = null;
        this.hoverBtn?.classList.remove('is-on');
        return;
      }
      const hit = pick(event);
      if (hit) {
        this.hoverIndex = hit.object.userData.index;
        this.syncPill(this.hoverIndex);
        this.hoverBtn?.classList.add('is-on');
      } else {
        this.hoverIndex = null;
        this.hoverBtn?.classList.remove('is-on');
      }
    };
    this.canvas.addEventListener('pointermove', this.onCanvasMove, { passive: true });

    // Click a frame → glide to it. On touch the details are NOT auto-opened:
    // the pinned "View Details" pill on the frame is the opener.
    this.onCanvasClick = (event) => {
      if (!this.classList.contains('is-entered') || this.classList.contains('is-reading')) return;
      const hit = pick(event);
      if (hit) this.goTo(hit.object.userData.index);
    };
    this.canvas.addEventListener('click', this.onCanvasClick);

    this.onKey = (event) => {
      if (this.classList.contains('is-diving')) return; // the dive owns the keyboard
      if (event.key === 'Escape' && this.classList.contains('is-reading')) return this.closeRead();
      if (!this.classList.contains('is-entered') || this.classList.contains('is-reading')) return;
      if (event.key === 'ArrowLeft') this.prev();
      else if (event.key === 'ArrowRight') this.next();
    };
    document.addEventListener('keydown', this.onKey);
  }

  goTo(i) {
    this.closeRead();
    this.atEnd = false;
    this.classList.remove('is-ending');
    this.index = clamp(i, 0, this.photos.length - 1);
    const wp = this.waypoints[this.index];
    this.goalPos.copy(wp.pos);
    this.goalLook.copy(wp.look);
    this.updateMeta();
  }

  next() {
    if (this.atEnd) return;
    if (this.index < this.photos.length - 1) this.goTo(this.index + 1);
    else {
      this.atEnd = true;
      this.classList.add('is-ending');
      this.goalPos.copy(this.endWaypoint.pos);
      this.goalLook.copy(this.endWaypoint.look);
    }
  }

  prev() {
    if (this.atEnd) { this.goTo(this.photos.length - 1); return; }
    if (this.index > 0) this.goTo(this.index - 1);
  }

  updateMeta() {
    const p = this.photos[this.index] || {};
    if (this.metaTitle) this.metaTitle.textContent = p.title || '';
    if (this.metaCount) {
      const n = this.photos.length;
      this.metaCount.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(n).padStart(2, '0')}`;
    }
  }

  openRead(i) {
    const p = this.photos[i] || {};
    if (!p.title && !p.story) return;
    this.readIndex = i;
    if (this.readTitle) this.readTitle.textContent = p.title || '';
    if (this.readStory) this.readStory.textContent = p.story || '';
    if (!this.touchUI) this.hoverBtn?.classList.remove('is-on'); // touch keeps the pill up as "Close"
    this.classList.add('is-reading');
    this.readEl?.setAttribute('aria-hidden', 'false');
  }

  closeRead() {
    this.readIndex = null;
    this.classList.remove('is-reading');
    this.readEl?.setAttribute('aria-hidden', 'true');
  }

  /* ======================================================================
     THE DIVE — "View Details" on a frame that carries an exhibit dossier.
     The photo is flown out of the wall and into the screen, a CRT switches on
     over it (degauss line → bloom → static → boot log), and the artifact's
     story is typeset as a vintage journal page that reveals part by part.
     Phase lives on the overlay's [data-phase]: off → zoom → boot → read → out.
     ====================================================================== */

  setupDive() {
    this.diveEl = this.querySelector('[data-museum-dive]');
    if (!this.diveEl) return;
    this.bootLogEl = this.querySelector('[data-museum-bootlog]');
    this.paperEl = this.querySelector('[data-museum-paper]');
    this.paperWrap = this.querySelector('[data-museum-paperwrap]');
    this.diveCloseBtn = this.querySelector('[data-museum-dive-close]');
    this.diveTimers = [];
    this.parts = [];

    this.onDiveClose = () => this.closeDive();
    this.diveCloseBtn?.addEventListener('click', this.onDiveClose);

    // No-WebGL fallback grid: its own "View Details" opens the same dive,
    // just without the fly-into-the-frame zoom (there is no 3D frame to leave).
    this.onFbDetails = (event) => {
      const btn = event.target instanceof Element && event.target.closest('[data-museum-fb-details]');
      if (btn) this.openDive(Number(btn.dataset.museumFbDetails), { zoom: false });
    };
    this.addEventListener('click', this.onFbDetails);

    // Anywhere on the boot screen skips the switch-on and cuts to the journal.
    this.onDiveSkip = (event) => {
      const phase = this.diveEl.dataset.phase;
      if (phase !== 'zoom' && phase !== 'boot') return;
      if (event.target instanceof Element && event.target.closest('[data-museum-dive-close]')) return;
      this.showPaper();
    };
    this.diveEl.addEventListener('click', this.onDiveSkip);

    // Capture phase: while the dive is up it owns Escape and the skip key.
    this.onDiveKey = (event) => {
      const phase = this.diveEl.dataset.phase;
      if (phase === 'off' || phase === 'out') return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.closeDive();
      } else if (phase === 'boot' && (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')) {
        this.showPaper();
      }
    };
    document.addEventListener('keydown', this.onDiveKey, true);
  }

  /** The About page's curved CRT glass, over the whole dive. The shared pass
      returns null under reduced motion or without WebGL, and the CSS tube in
      the markup stands in — so this is only ever attempted once. */
  startDiveCrt() {
    if (this.diveCrt) return this.diveCrt.start();
    if (this.diveCrtTried) return;
    this.diveCrtTried = true;
    this.diveCrt = window.AnemoiaDesktopFX?.initCrt?.(this) || null;
  }

  /** Fly the GALLERY into the artwork — the same move the About and World pages
      make into their monitor, and the one `flyIntoDoor` makes into the house
      door. With transform-origin O and `translate(T) scale(s)`, the point at O
      lands at O + T, so translating by (screen centre − O) puts the artwork dead
      centre however the viewport is shaped. */
  flyIntoFrame(rect) {
    const canvas = this.canvas;
    if (!canvas || !rect) return false;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    const zoom = Number(this.dataset.diveZoom) || 4.5;
    canvas.style.transformOrigin = `${cx.toFixed(1)}px ${cy.toFixed(1)}px`;
    void canvas.offsetWidth; // commit the origin before the transform animates
    canvas.style.transform = `translate(${(vw / 2 - cx).toFixed(1)}px, ${(vh / 2 - cy).toFixed(1)}px) scale(${zoom})`;
    return true;
  }

  hasDossier(i) {
    return Boolean(this.diveEl && this.querySelector(`[data-museum-dossier="${i}"]`));
  }

  clearDiveTimers() {
    this.diveTimers?.forEach((t) => clearTimeout(t));
    this.diveTimers = [];
    if (this.revealTimer) { clearTimeout(this.revealTimer); this.revealTimer = null; }
  }

  openDive(i, opts = {}) {
    const tpl = this.querySelector(`[data-museum-dossier="${i}"]`);
    if (!tpl || !this.diveEl || this.classList.contains('is-diving')) return;

    this.clearDiveTimers();
    this.closeRead();
    this.diveIndex = i;
    this.classList.add('is-diving');
    document.documentElement.classList.add('museum-dive-lock');

    // Load the journal page (the dossier markup is rendered by Liquid).
    this.paperEl.replaceChildren(tpl.content.cloneNode(true));
    this.parts = Array.from(this.paperEl.querySelectorAll('[data-part]'));
    this.paperWrap.scrollTop = 0;
    this.bootLogEl.textContent = '';
    this.diveEl.classList.remove('is-read-through');
    this.diveEl.setAttribute('aria-hidden', 'false');

    if (prefersReducedMotion()) {
      this.pauseScene();
      this.diveEl.dataset.phase = 'read';
      this.startReveal();
      return;
    }

    // A frame that is tiny or off to the side (mid-glide) has no useful rect to
    // fly into — those just cut straight to the switch-on.
    const rect = opts.zoom === false ? null : this.projectRect(i);
    if (!rect || rect.w < 8 || rect.h < 8 || !this.flyIntoFrame(rect)) return this.runBoot();

    // The scene KEEPS RENDERING through the fly-in. A WebGL canvas that has
    // stopped drawing can composite empty once something (here, its own CSS
    // transform) forces a new composite — so it is only paused at the switch-on,
    // by which point the veil is black anyway.
    this.diveEl.dataset.phase = 'zoom';
    this.diveTimers.push(window.setTimeout(() => this.runBoot(), 820));
  }

  /** The CRT switch-on. CSS runs the degauss line / bloom / static / roll; this
      types the boot log underneath it, then hands over to the journal. */
  runBoot() {
    if (!this.diveEl || this.diveEl.dataset.phase === 'read') return;
    this.diveEl.dataset.phase = 'boot';
    this.pauseScene(); // safe now: the veil is black over the gallery
    this.startDiveCrt(); // the glass switches on with the picture, not during the flight
    this.playDegauss();

    const heading = this.paperEl.querySelector('.jrn__title')?.textContent?.trim() || 'exhibit';
    const kicker = this.paperEl.querySelector('.jrn__kicker')?.textContent?.trim() || 'exhibit';
    const lines = [
      [['b-key', this.dataset.bootSystem || 'ANEMOIA ARCHIVE SYSTEM']],
      [['', ' ']],
      [['', 'degauss coil ......... '], ['b-ok', 'ok']],
      [['', 'phosphor warm ........ '], ['b-ok', 'ok']],
      [['', 'vertical hold ........ '], ['b-ok', 'locked']],
      [['', `mounting ${kicker}`]],
      [['', 'artifact ............. '], ['b-key', heading]],
      [['', ' ']],
      [['b-caret', '> '], ['', 'play memory']],
    ];

    lines.forEach((spans, li) => {
      this.diveTimers.push(window.setTimeout(() => {
        const frag = document.createDocumentFragment();
        spans.forEach(([cls, text]) => {
          const s = document.createElement('span');
          if (cls) s.className = cls;
          s.textContent = text; // exhibit copy is merchant text — never innerHTML
          frag.appendChild(s);
        });
        frag.appendChild(document.createTextNode('\n'));
        this.bootLogEl.appendChild(frag);
      }, 300 + li * 165));
    });

    this.diveTimers.push(window.setTimeout(() => this.showPaper(), 300 + lines.length * 165 + 430));
  }

  showPaper() {
    if (!this.diveEl || this.diveEl.dataset.phase === 'read') return;
    this.clearDiveTimers();
    this.diveEl.dataset.phase = 'read';
    this.startReveal();
    this.diveCloseBtn?.focus?.({ preventScroll: true });
  }

  /** Reveal the journal part by part: whatever is on screen cascades in on a
      timer (so it plays like an explainer), the rest lock in as you scroll. */
  startReveal() {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      this.parts.forEach((p) => p.classList.add('is-in'));
      this.diveEl.classList.add('is-read-through');
      return;
    }

    this.revealQueue = [];
    this.revealTimer = null;
    // The first batch waits for the tube to finish opening; later batches (the
    // ones you scroll into) lock in almost immediately.
    this.firstDrain = true;

    const drain = () => {
      const el = this.revealQueue.shift();
      if (el) el.classList.add('is-in');
      this.revealTimer = this.revealQueue.length ? window.setTimeout(drain, 260) : null;
      if (this.parts.every((p) => p.classList.contains('is-in'))) this.diveEl.classList.add('is-read-through');
    };

    const kick = () => {
      if (!this.revealQueue.length || this.revealTimer) return;
      this.revealTimer = window.setTimeout(drain, this.firstDrain ? 430 : 130);
      this.firstDrain = false;
    };

    this.partObserver?.disconnect();
    this.partObserver = new IntersectionObserver((entries) => {
      entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => this.parts.indexOf(a.target) - this.parts.indexOf(b.target))
        .forEach((e) => {
          this.partObserver.unobserve(e.target);
          this.revealQueue.push(e.target);
        });
      kick();
    }, { root: this.paperWrap, rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    this.parts.forEach((p) => this.partObserver.observe(p));
  }

  closeDive() {
    if (!this.diveEl || this.diveEl.dataset.phase === 'off' || this.diveEl.dataset.phase === 'out') return;
    this.clearDiveTimers();
    this.partObserver?.disconnect();
    this.diveEl.dataset.phase = 'out'; // the tube collapses to a line, then a dot

    const done = () => {
      this.diveEl.dataset.phase = 'off';
      this.diveEl.setAttribute('aria-hidden', 'true');
      this.diveEl.classList.remove('is-read-through');
      this.paperEl.replaceChildren();
      this.bootLogEl.textContent = '';
      this.parts = [];
      this.classList.remove('is-diving');
      // Removing is-diving drops the transition too, so the gallery snaps back
      // to its normal framing behind the fading overlay rather than zooming out.
      if (this.canvas) {
        this.canvas.style.transform = '';
        this.canvas.style.transformOrigin = '';
      }
      document.documentElement.classList.remove('museum-dive-lock');
      this.diveCrt?.stop(); // hand the GPU back to the gallery
      this.resumeScene();
      if (this.hoverBtn?.classList.contains('is-on')) this.hoverBtn.focus?.({ preventScroll: true });
    };

    if (prefersReducedMotion()) done();
    else this.diveTimers.push(window.setTimeout(done, 440));
  }

  /** The coil thump a tube makes when it wakes up. Synthesised, so there is
      nothing to upload — and only ever heard if the visitor turned sound ON
      (unset means silence on this page; see the site-wide audio rule). */
  playDegauss() {
    try {
      if (localStorage.getItem('anemoia:sound') !== 'on') return;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!this.audioCtx) this.audioCtx = new Ctx();
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') ctx.resume();
      const t = ctx.currentTime;

      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(124, t);
      osc.frequency.exponentialRampToValueAtTime(38, t + 0.5);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.2, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
      osc.connect(g).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.75);

      const len = Math.floor(ctx.sampleRate * 0.45);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 2600;
      bp.Q.value = 0.7;
      const ng = ctx.createGain();
      ng.gain.value = 0.05;
      noise.connect(bp).connect(ng).connect(ctx.destination);
      noise.start(t);
    } catch (e) {
      /* sound is a garnish — never let it break the dive */
    }
  }

  pauseScene() {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
  }

  resumeScene() {
    if (this.booted && !this.raf && !document.hidden && !this.classList.contains('is-diving')) {
      this.raf = requestAnimationFrame(this.animate);
    }
  }

  /** Projected screen rect (px) of photo i's plane — used to glue the HTML
      pill/description onto the frame inside the 3D scene. */
  projectRect(i) {
    const THREE = window.THREE;
    const mesh = this.artMeshes?.[i];
    if (!mesh) return null;
    const { pw, ph } = mesh.userData;
    const w = this.clientWidth;
    const h = this.clientHeight;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const v = new THREE.Vector3();
    for (const [sx, sy] of [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]]) {
      v.set(sx * pw, sy * ph, 0);
      mesh.localToWorld(v);
      v.project(this.camera);
      const x = ((v.x + 1) / 2) * w;
      const y = ((1 - v.y) / 2) * h;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  /** Label and locked state of the in-frame pill for artwork i. A curtained
      frame greys the pill out, relabels it, and disables it outright so the
      click cannot land even if something else re-enables pointer events. */
  syncPill(i) {
    const btn = this.hoverBtn;
    if (!btn) return;
    const locked = i != null && Boolean(this.photos?.[i]?.locked);
    let label;
    if (locked) label = this.dataset.lockedLabel || 'Locked';
    else if (this.touchUI && this.classList.contains('is-reading')) label = 'Close';
    else label = this.dataset.detailsLabel || 'View Details';
    if (btn.textContent !== label) btn.textContent = label;
    btn.classList.toggle('is-locked', locked);
    btn.disabled = locked;
  }

  /** Keep the hover pill + on-frame description glued to their frames. */
  updateOverlays() {
    if (this.touchUI && this.hoverBtn) {
      // Touch: the pill rides the CURRENT frame's bottom centre and flips to
      // "Close" while the description is open (it's the only close control).
      const on = this.classList.contains('is-entered') && !this.atEnd;
      this.hoverBtn.classList.toggle('is-on', on);
      if (on) {
        this.syncPill(this.index);
        const r = this.projectRect(this.index);
        if (r) {
          this.hoverBtn.style.left = `${(r.x + r.w / 2).toFixed(1)}px`;
          this.hoverBtn.style.top = `${(r.y + r.h * 0.9).toFixed(1)}px`;
        }
      }
    } else if (this.hoverBtn?.classList.contains('is-on') && this.hoverIndex != null) {
      const r = this.projectRect(this.hoverIndex);
      if (r) {
        this.hoverBtn.style.left = `${(r.x + r.w / 2).toFixed(1)}px`;
        this.hoverBtn.style.top = `${(r.y + r.h * 0.84).toFixed(1)}px`;
      }
    }
    if (this.readEl && this.readIndex != null && this.classList.contains('is-reading')) {
      const r = this.projectRect(this.readIndex);
      if (r) {
        this.readEl.style.left = `${r.x.toFixed(1)}px`;
        this.readEl.style.top = `${r.y.toFixed(1)}px`;
        this.readEl.style.width = `${r.w.toFixed(1)}px`;
        this.readEl.style.height = `${r.h.toFixed(1)}px`;
      }
    }
  }

  resize() {
    const w = this.clientWidth || window.innerWidth;
    const h = this.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.fitFrame();
  }

  /**
   * Keep the whole artwork on screen on a narrow viewport.
   *
   * The field of view is VERTICAL, so a portrait phone sees a much narrower
   * slice of the world than a laptop does at the same distance: the gold frame
   * grew wider than the screen and its side rails were cut clean off. This
   * steps the camera back — never the fov, which would bow the perspective —
   * until the frame plus a margin fits across, and it can only ever move the
   * camera FURTHER than the pinned `cam_dist`. On anything wider than roughly
   * square the guard is inactive and the locked framing is untouched.
   */
  fitFrame() {
    if (!this.waypoints?.length || !this.artW) return;
    const halfV = Math.tan((this.camera.fov * Math.PI) / 180 / 2);
    const aspect = this.camera.aspect || 1;
    const needW = (this.artW * 1.3) / 2;  // frame width plus real breathing room
    const needH = (this.artH * 1.12) / 2;
    /* Capped, and the cap is load-bearing: the camera has to stay in the aisle.
       A mis-measured viewport once put it 23 units back, out through the window
       wall and looking at the house from the field. Backing off at most 3 units
       keeps it inside the room whatever the measurement says — a squeezed frame
       is a blemish, a camera outside the building is a broken page. */
    const wanted = Math.max(this.camDistBase, needW / (halfV * aspect), needH / halfV);
    const dist = Math.min(wanted, this.camDistBase + 3);
    if (Math.abs(dist - this.camDist) < 0.01) return;

    this.camDist = dist;
    const x = this.wallX - dist;
    this.waypoints.forEach((wp) => { wp.pos.x = x; });
    if (this.endWaypoint) this.endWaypoint.pos.x = x;
    // Re-aim whatever the camera is currently travelling to, or it would keep
    // gliding to the old distance until the next arrow press.
    if (this.goalPos) this.goalPos.x = x;
  }

  /** A slow breath through the curtains. The cloth pushes toward the room and
      sways a little, held still at the rod so it never lifts off it — the sway
      grows as the square of the drop, which is roughly how hanging fabric
      behaves and keeps the top few centimetres quiet. */
  breatheCurtains(t) {
    for (const c of this.curtains) {
      const pos = c.geometry.attributes.position;
      const rest = c.userData.rest;
      const { ch, amp, phase } = c.userData;
      for (let k = 0; k < pos.count; k++) {
        const x = rest[k * 3];
        const y = rest[k * 3 + 1];
        const drop = clamp((ch / 2 - y) / ch, 0, 1); // 0 at the rod, 1 at the hem
        const hang = Math.max(0, drop - 0.09) / 0.91;
        const a = amp * hang * hang;
        pos.array[k * 3] = x + a * 0.3 * Math.sin(t * 0.55 + phase + drop * 2.4);
        pos.array[k * 3 + 2] = a * Math.sin(x * 2.4 + t * 0.85 + phase) * Math.cos(drop * 1.9 - t * 0.4);
      }
      pos.needsUpdate = true;
    }
  }

  animate() {
    if (this.curtains?.length && !prefersReducedMotion()) {
      this.breatheCurtains(performance.now() / 1000);
    }
    // Smooth glide: ease the camera position and its look target separately
    // (the look eases a touch faster, which reads like a natural head-turn).
    this.camPos.lerp(this.goalPos, 0.05);
    this.camLook.lerp(this.goalLook, 0.075);
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.camLook);
    this.updateOverlays();
    this.renderer.render(this.scene, this.camera);
    this.raf = requestAnimationFrame(this.animate);
  }

  /** adampointer.png follower, matching the other Anemoia pages. */
  setupCursor() {
    if (!this.dataset.pointer || !window.matchMedia('(pointer: fine)').matches) return;
    this.cursorEl = document.createElement('div');
    this.cursorEl.className = 'museum__cursor';
    this.cursorEl.setAttribute('aria-hidden', 'true');
    this.appendChild(this.cursorEl);
    this.classList.add('museum--cursor');
    this.onCursorMove = (event) => {
      this.cursorEl.style.left = `${event.clientX}px`;
      this.cursorEl.style.top = `${event.clientY}px`;
      this.cursorEl.classList.add('is-visible');
      const over = event.target instanceof Element && event.target.closest('a, button, canvas');
      this.cursorEl.classList.toggle('is-over', Boolean(over));
    };
    window.addEventListener('pointermove', this.onCursorMove, { passive: true });
  }

  disconnectedCallback() {
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.enterTimer) clearTimeout(this.enterTimer);
    this.clearDiveTimers();
    this.partObserver?.disconnect();
    document.documentElement.classList.remove('museum-dive-lock');
    if (this.onDiveKey) document.removeEventListener('keydown', this.onDiveKey, true);
    if (this.onDiveClose) this.diveCloseBtn?.removeEventListener('click', this.onDiveClose);
    if (this.onDiveSkip) this.diveEl?.removeEventListener('click', this.onDiveSkip);
    if (this.onFbDetails) this.removeEventListener('click', this.onFbDetails);
    this.diveCrt?.stop();
    this.audioCtx?.close?.();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.onKey) document.removeEventListener('keydown', this.onKey);
    if (this.onCanvasClick) this.canvas?.removeEventListener('click', this.onCanvasClick);
    if (this.onCanvasMove) this.canvas?.removeEventListener('pointermove', this.onCanvasMove);
    if (this.onKnock) this.houseEl?.removeEventListener('click', this.onKnock);
    if (this.onHouseResize) window.removeEventListener('resize', this.onHouseResize);
    if (this.onCursorMove) window.removeEventListener('pointermove', this.onCursorMove);
    if (this.cursorEl) this.cursorEl.remove();
    this.renderer?.dispose?.();
  }
}

if (!customElements.get('anemoia-museum')) {
  customElements.define('anemoia-museum', AnemoiaMuseum);
}
